
import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Info } from "lucide-react";
import { 
  CapTableEntry,
} from "@/lib/types";
import { calculateFullyDilutedOwnership } from "@/lib/capital-calculations";

interface CapTableProps {
  capTable: CapTableEntry[];
  setCapTable: React.Dispatch<React.SetStateAction<CapTableEntry[]>>;
}

export function CapTable({ capTable, setCapTable }: CapTableProps) {
  const [newShareholder, setNewShareholder] = useState<Partial<CapTableEntry>>({
    shareholderType: "investor",
    shareClass: "ordinary",
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Calculate totals
  const totalShares = capTable.reduce((sum, entry) => sum + entry.sharesIssued, 0);
  const totalValue = capTable.reduce((sum, entry) => sum + (entry.value || 0), 0);

  const handleAddShareholder = () => {
    if (!newShareholder.shareholderName || !newShareholder.sharesIssued || !newShareholder.issuePrice) {
      // In a real app, you'd show validation errors
      return;
    }

    // Create a new shareholder entry with calculated fields
    const newEntry: CapTableEntry = {
      id: `${Date.now()}`, // Generate a unique ID
      shareholderName: newShareholder.shareholderName || "",
      shareholderType: newShareholder.shareholderType as any || "investor",
      shareClass: newShareholder.shareClass as any || "ordinary",
      sharesIssued: Number(newShareholder.sharesIssued) || 0,
      issuePrice: Number(newShareholder.issuePrice) || 0,
      issueDate: newShareholder.issueDate || new Date().toISOString().split('T')[0],
      dilutedOwnership: 0, // Will be calculated
      value: (Number(newShareholder.sharesIssued) || 0) * (Number(newShareholder.issuePrice) || 0)
    };

    // Add to cap table and recalculate percentages
    const updatedCapTable = [...capTable, newEntry];
    const finalCapTable = calculateFullyDilutedOwnership(updatedCapTable);
    
    setCapTable(finalCapTable);
    setIsAddDialogOpen(false);
    setNewShareholder({
      shareholderType: "investor",
      shareClass: "ordinary",
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Cap Table</CardTitle>
          <CardDescription>
            Track company ownership and share distribution
          </CardDescription>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" /> Add Shareholder
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Shareholder</DialogTitle>
              <DialogDescription>
                Enter details about the new shareholder to add them to the cap table.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Shareholder Name</Label>
                <Input
                  id="name"
                  value={newShareholder.shareholderName || ""}
                  onChange={(e) => setNewShareholder({...newShareholder, shareholderName: e.target.value})}
                  placeholder="Investor name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="type">Type</Label>
                  <Select 
                    value={newShareholder.shareholderType} 
                    onValueChange={(value) => setNewShareholder({...newShareholder, shareholderType: value as any})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="founder">Founder</SelectItem>
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="class">Share Class</Label>
                  <Select 
                    value={newShareholder.shareClass} 
                    onValueChange={(value) => setNewShareholder({...newShareholder, shareClass: value as any})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ordinary">Ordinary</SelectItem>
                      <SelectItem value="preference">Preference</SelectItem>
                      <SelectItem value="options">Options</SelectItem>
                      <SelectItem value="esop">ESOP</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="shares">Shares Issued</Label>
                  <Input
                    id="shares"
                    type="number"
                    value={newShareholder.sharesIssued || ""}
                    onChange={(e) => setNewShareholder({...newShareholder, sharesIssued: Number(e.target.value)})}
                    placeholder="Number of shares"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="price">Issue Price</Label>
                  <Input
                    id="price"
                    type="number"
                    value={newShareholder.issuePrice || ""}
                    onChange={(e) => setNewShareholder({...newShareholder, issuePrice: Number(e.target.value)})}
                    placeholder="Price per share"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="date">Issue Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={newShareholder.issueDate || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setNewShareholder({...newShareholder, issueDate: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddShareholder}>Add Shareholder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Shareholder</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Class</TableHead>
              <TableHead className="text-right">Shares</TableHead>
              <TableHead className="text-right">Issue Price</TableHead>
              <TableHead className="text-right">Issue Date</TableHead>
              <TableHead className="text-right">Ownership %</TableHead>
              <TableHead className="text-right">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {capTable.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.shareholderName}</TableCell>
                <TableCell className="capitalize">{entry.shareholderType}</TableCell>
                <TableCell className="capitalize">{entry.shareClass}</TableCell>
                <TableCell className="text-right">{entry.sharesIssued.toLocaleString()}</TableCell>
                <TableCell className="text-right">${entry.issuePrice.toFixed(3)}</TableCell>
                <TableCell className="text-right">{new Date(entry.issueDate).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">{entry.dilutedOwnership?.toFixed(2)}%</TableCell>
                <TableCell className="text-right">${(entry.value || 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-medium">
              <TableCell colSpan={3}>Total</TableCell>
              <TableCell className="text-right">{totalShares.toLocaleString()}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell className="text-right">100.00%</TableCell>
              <TableCell className="text-right">${totalValue.toLocaleString()}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
